import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowLeft, Sun, Moon } from "lucide-react";
import { Link } from "react-router-dom";
import { Logo } from "@/components/Logo";

export const spring = { type: "spring", damping: 25, stiffness: 200 };

export const CARD_SHADOW = "shadow-[0_2px_10px_rgba(0,0,0,0.02),0_10px_30px_rgba(0,0,0,0.04)]";

export const THEME_KEY = "doclyx_ws_theme";

export const useUITheme = () => {
  const [theme, setTheme] = useState(() => {
    const stored = localStorage.getItem(THEME_KEY);
    if (stored) return stored;
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  });
  const toggle = () => {
    const next = theme === "dark" ? "light" : "dark";
    setTheme(next);
    localStorage.setItem(THEME_KEY, next);
  };
  return { theme, dark: theme === "dark", toggle };
};

export const ThemeToggle = ({ dark, onToggle, testid = "theme-toggle" }) => (
  <button
    type="button"
    onClick={onToggle}
    data-testid={testid}
    aria-label={dark ? "Switch to light mode" : "Switch to dark mode"}
    className={`w-10 h-10 rounded-full border flex items-center justify-center active:scale-95 transition-transform duration-150 ${
      dark ? "bg-white/[0.08] border-white/[0.1]" : "bg-black/[0.04] border-black/[0.05]"
    }`}
  >
    {dark ? <Sun className="w-4 h-4 text-white/80" strokeWidth={1.5} /> : <Moon className="w-4 h-4 text-[#3C3C43]/70" strokeWidth={1.5} />}
  </button>
);

export const pageTheme = (dk) => ({
  page: dk ? "v4-theme ws-root bg-[#050505] text-white" : "bg-[#F2F2F7] text-[#0A0A0A]",
  card: dk ? "bg-[#1C1C1E] border border-white/[0.08]" : `bg-white ${CARD_SHADOW}`,
  primary: dk ? "text-white" : "text-[#0A0A0A]",
  secondary: dk ? "text-white/55" : "text-[#3C3C43]/60",
  tertiary: dk ? "text-white/35" : "text-[#3C3C43]/40",
  link: dk ? "text-[#0A84FF]" : "text-[#002B5B]",
  linkMuted: dk ? "text-white/55 hover:text-white" : "text-[#3C3C43]/60 hover:text-[#0A0A0A]",
  primaryBtn: dk ? "bg-[#0A84FF] text-white" : "bg-[#002B5B] text-white",
  secondaryBtn: dk ? "bg-white/[0.08] text-white" : "bg-black/[0.05] text-[#0A0A0A]",
  input: dk
    ? "bg-white/[0.06] text-white placeholder:text-white/30 focus:ring-[#0A84FF]/40 focus:bg-white/[0.09]"
    : "bg-black/[0.04] text-[#0A0A0A] placeholder:text-[#3C3C43]/35 focus:ring-[#002B5B]/25 focus:bg-white",
  noticeBox: dk ? "bg-[#30D158]/[0.12] border-[#30D158]/25" : "bg-[#34C759]/[0.08] border-[#34C759]/20",
  noticeText: dk ? "text-[#30D158]" : "text-[#248A3D]",
  darkPanel: dk ? "bg-white/[0.05]" : "bg-black/[0.04]",
});

export const SegmentedControl = ({ options, value, onChange, testid }) => (
  <div className="inline-flex bg-[#767680]/[0.12] p-0.5 rounded-full" data-testid={testid}>
    {options.map((o) => (
      <button
        key={o.value}
        type="button"
        data-testid={`${testid}-${o.value}`}
        onClick={() => onChange(o.value)}
        className={`px-4 min-h-[32px] text-[13px] rounded-full transition-[background-color,color,box-shadow] duration-200 ${
          value === o.value ? "bg-white shadow-sm font-semibold text-[#0A0A0A]" : "text-[#3C3C43]/60 hover:text-[#0A0A0A]"
        }`}
      >
        {o.label}
      </button>
    ))}
  </div>
);

export const IOSButton = ({ variant = "primary", className = "", ...props }) => {
  const styles = {
    primary: "bg-[#002B5B] text-white shadow-[0_1px_2px_rgba(0,0,0,0.05)]",
    plain: "bg-black/[0.05] text-[#002B5B]",
    destructive: "bg-black/[0.05] text-[#FF3B30]",
  };
  return (
    <button
      type="button"
      className={`inline-flex items-center justify-center gap-2 min-h-[44px] px-5 rounded-full text-[15px] font-semibold transition-colors duration-200 disabled:opacity-40 disabled:pointer-events-none ${styles[variant]} ${className}`}
      {...props}
    />
  );
};

const inputClass =
  "w-full min-h-[44px] rounded-xl bg-black/[0.04] border border-transparent px-4 text-[15px] text-[#0A0A0A] placeholder:text-[#3C3C43]/35 focus:outline-none focus:ring-2 focus:ring-[#002B5B]/25 focus:bg-white transition-[background-color,box-shadow] duration-200";

export const Field = ({ label, testid, className = "", ...props }) => (
  <label className="block">
    <span className="block text-[13px] font-medium text-[#3C3C43]/70 mb-1.5">{label}</span>
    <input data-testid={testid} className={`${inputClass} ${className}`} {...props} />
  </label>
);

export const TextArea = ({ label, testid, rows = 5, className = "", ...props }) => (
  <label className="block">
    <span className="block text-[13px] font-medium text-[#3C3C43]/70 mb-1.5">{label}</span>
    <textarea data-testid={testid} rows={rows} className={`${inputClass} py-3 min-h-0 leading-relaxed ${className}`} {...props} />
  </label>
);

const STATUS_STYLES = {
  draft: "bg-black/[0.06] text-[#3C3C43]/70",
  open: "bg-[#002B5B]/10 text-[#002B5B]",
  sent: "bg-[#002B5B]/10 text-[#002B5B]",
  paid: "bg-[#34C759]/[0.14] text-[#248A3D]",
  signed: "bg-[#34C759]/[0.14] text-[#248A3D]",
  completed: "bg-[#9B703F]/[0.14] text-[#9B703F]",
  void: "bg-[#FF3B30]/10 text-[#FF3B30]",
  overdue: "bg-[#FF3B30]/10 text-[#FF3B30]",
};

export const StatusBadge = ({ status }) => (
  <span
    data-testid={`status-badge-${status}`}
    className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold capitalize ${STATUS_STYLES[status] || STATUS_STYLES.draft}`}
  >
    {status}
  </span>
);

export const InsetList = ({ children, testid }) => (
  <div className={`bg-white rounded-sm border border-hairline overflow-hidden divide-y divide-black/[0.05] ${CARD_SHADOW}`} data-testid={testid}>
    {children}
  </div>
);

export const Sheet = ({ open, onClose, title, children, testid, wide = false }) => {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  return (
  <AnimatePresence>
    {open && (
      <motion.div
        className="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <div className="absolute inset-0 bg-black/60" onClick={onClose} data-testid={`${testid}-backdrop`} />
        <motion.div
          initial={{ y: 80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 80, opacity: 0 }}
          transition={spring}
          className={`relative w-full ${wide ? "sm:max-w-2xl" : "sm:max-w-lg"} bg-white border border-hairline px-6 pb-8 pt-4 max-h-[88vh] overflow-y-auto`}
          data-testid={testid}
        >
          <div className="w-9 h-1.5 rounded-full bg-black/15 mx-auto mb-4 sm:hidden" />
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-lg font-semibold tracking-tight text-[#0A0A0A]">{title}</h3>
            <button
              onClick={onClose}
              data-testid={`${testid}-close`}
              className="w-8 h-8 rounded-full bg-black/[0.05] flex items-center justify-center active:scale-95 transition-transform duration-150"
              aria-label="Close"
            >
              <X className="w-4 h-4 text-[#3C3C43]/70" strokeWidth={2} />
            </button>
          </div>
          {children}
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
  );
};

export const V5Shell = ({ title, subtitle, children, testid }) => (
  <div className="min-h-screen bg-[#F2F2F7] text-[#0A0A0A] font-sans" data-testid={testid}>
    <header className="sticky top-0 z-40 bg-page border-b border-hairline">
      <div className="max-w-6xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between gap-4">
        <Logo size={26} />
        <Link
          to="/admin"
          data-testid="v5-back-link"
          className="flex items-center gap-1.5 text-[13px] font-medium text-[#002B5B] min-h-[44px] active:scale-95 transition-transform duration-150"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> {subtitle}
        </Link>
      </div>
    </header>
    <main className="max-w-6xl mx-auto px-5 md:px-8 py-8 md:py-12">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={spring}>
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight mb-8" data-testid={`${testid}-title`}>
          {title}
        </h1>
        {children}
      </motion.div>
    </main>
  </div>
);
